import { useState, useEffect } from 'react';

import {
  Grid,
  Typography,
  Fab,
  Paper,
  TextField,
  Box,
  useTheme,
} from '@mui/material';
import { ArrowUpward as ArrowUpwardIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';

import ClientInfo from './ClientInfo';
import DetailedInfo from './DetailedInfo';
import {
  AcceptedTable,
  IncomeTable,
  FinancialBurdenTable,
  AnimalProductionTable,
  CropProductionTable,
  TerytTable,
  TabPanelTable,
} from './Tables';
import { Tab, Tabs } from '../../components/common/Tabs';
import { Copyright, GreenSwitch } from '../../components/common';
import colors from '../../assets/colors';
import { loan, user, directPayment } from '../../utils/api';

export default function IndividualClient() {
  const theme = useTheme();
  const id = window.location.pathname.split('/').pop();
  const startYear = 2018;
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );
  const [value, setValue] = useState(4);
  const [hideFab, setHideFab] = useState(true);
  const [notes, setNotes] = useState();
  const [noteValue, setNoteValue] = useState('');
  const [editNotes, setEditNotes] = useState(false);
  const [applicantData, setApplicantData] = useState();
  const [incomes, setIncomes] = useState();
  const [liabilities, setLiabilities] = useState();
  const [teryt, setTeryt] = useState();
  const [directPaymentData, setDirectPaymentData] = useState();
  const [yearlyIncome, setYearlyIncome] = useState([]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    directPayment.year(applicantData.farmId, years[newValue]).then((res) => {
      setDirectPaymentData(res.data[0]);
    });
  };

  useEffect(() => {
    user
      .getNotes(id)
      .then((result) => {
        if (result.data.length > 0) {
          setNotes(result.data?.sort((a, b) => a.id - b.id));
          setNoteValue(result.data?.sort((a, b) => a.id - b.id)[0].note || '');
        } else user.addNote(id, { notes: 'Dodaj notatkę...' });
      })
      .catch((err) => {});
    loan
      .getData(id)
      .then((res) => {
        setApplicantData(res.data);
        directPayment
          .year(res.data.farmId, res.data.directPaymentYear)
          .then((res) => {
            setDirectPaymentData(res.data[0]);
          });
        setValue(years.indexOf(res.data.directPaymentYear));
        directPayment
          .fields(res.data.directPaymentId, res.data.farmId)
          .then((res) => setTeryt(res.data));
        setYearlyIncome([]);
        years.forEach((y) => {
          directPayment.year(res.data.farmId, y).then((yearlyRes) => {
            setYearlyIncome((prev) => [
              ...prev,
              {
                name: y,
                uv: yearlyRes.data[0]?.productionValue || 0,
                pv: 2400,
                amt: 2400,
              },
            ]);
          });
        });
      })
      .catch((err) => {});
    loan
      .getIncome(id)
      .then((res) => setIncomes(res.data))
      .catch((err) => {});
    loan
      .getLiabilities(id)
      .then((res) => setLiabilities(res.data))
      .catch((err) => {});
    //eslint-disable-next-line
  }, [id]);

  const editNote = () => {
    user
      .editNote(notes[0].id, { note: noteValue })
      .then((result) => {})
      .catch((err) => {});
  };

  useEffect(() => {
    const onScrollHandler = (e) => {
      if (document.getElementById('dashboard_main').scrollTop >= 400)
        setHideFab(false);
      else setHideFab(true);
    };
    document
      .getElementById('dashboard_main')
      .addEventListener('scroll', onScrollHandler);

    return () =>
      document
        .getElementById('dashboard_main')
        ?.removeEventListener('scroll', onScrollHandler);
  }, []);

  const scrollToTop = () => {
    document.getElementById('dashboard_main').scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Box sx={{ px: 5 }}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 10 }}
        >
          <ClientInfo data={applicantData} />
          <DetailedInfo data={applicantData} />
        </Grid>
        <Paper sx={{ maxWidth: 200, py: 1, px: 2, mt: 4 }}>
          <Grid container>
            <Typography sx={{ mt: 0.75 }}>Edytuj notatki</Typography>
            <GreenSwitch
              onChange={(e) => {
                setEditNotes(e.target.checked);
                if (!e.target.checked) editNote();
              }}
              aria-label="editNotes"
            />
          </Grid>
        </Paper>
        <Paper sx={{ p: 2, mt: 2 }}>
          {editNotes ? (
            <TextField
              sx={{ width: '100%' }}
              multiline
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              inputProps={{ 'data-testid': 'editableNotes' }}
            />
          ) : (
            <Typography
              aria-label="staticNotes"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {noteValue}
            </Typography>
          )}
        </Paper>

        <AcceptedTable data={applicantData} />
        <IncomeTable data={applicantData} incomes={incomes} />
        {liabilities && liabilities?.length > 0 && (
          <FinancialBurdenTable data={liabilities} />
        )}
        {incomes && incomes?.loanAnimalIncomeResponseList.length > 0 && (
          <AnimalProductionTable data={incomes.loanAnimalIncomeResponseList} />
        )}
        {incomes && incomes?.loanCropIncomeResponses.length > 0 && (
          <CropProductionTable data={incomes.loanCropIncomeResponses} />
        )}
        <TerytTable data={teryt} />

        {!hideFab && (
          <Fab
            aria-label="fab"
            style={{
              position: 'absolute',
              bottom: 50,
              right: 100,
              color: '#FFFFFF',
              backgroundColor: '#118553',
            }}
            onClick={scrollToTop}
          >
            <ArrowUpwardIcon />
          </Fab>
        )}

        <Grid container justifyContent="center" sx={{ mb: 4 }}>
          <Typography sx={{ mb: 2 }}>
            Dochodowość gospodarstwa na przestrzeni lat
          </Typography>
          <ResponsiveContainer width={'100%'} height={300}>
            <LineChart data={yearlyIncome.sort((a, b) => a.name - b.name)}>
              <Line type="linear" dataKey="uv" stroke="#8884d8" />
              <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
              <XAxis dataKey="name" />
              <YAxis />
            </LineChart>
          </ResponsiveContainer>
        </Grid>

        <Box
          sx={{
            flexGrow: 1,
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            border: 2,
            borderRadius: '10px',
            borderColor: colors.agroweGreen,
            boxShadow: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: '100.1%',
              height: '100%',
              borderRadius: '10px 10px 0px 0px',
              bgcolor: colors.oldAgroweGreen,
              background: colors.agroweGreen,
              mt: -0.1,
              ml: -0.1,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons={true}
              allowScrollButtonsMobile
              aria-label="scrollable force tabs example"
              sx={{ borderRadius: '10px 10px 0px 0px' }}
            >
              {years.map((year, index) => (
                <Tab yellow label={year} key={year} />
              ))}
            </Tabs>
          </Box>
          <TabPanelTable data={directPaymentData?.groupedCultivationsList} />
        </Box>

        <Box component={Paper} sx={{ py: 3, px: 8 }}>
          <Copyright />
        </Box>
      </Box>
    </>
  );
}
